import { App } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiPost, errorMessage, qk } from "@/shared/api";

/**
 * Модерация отзыва: публикация, скрытие, удаление и ответ поддержки
 * (доработки §6.2, §7 «Comments»).
 *
 * Четыре мутации одним хуком, потому что у них одна зона инвалидации:
 * очередь, карточка отзыва, счётчик на сводке и журнал действий. Журнал
 * инвалидируется всегда — §6.2 требует, чтобы скрытие с причиной читалось в
 * аудите, и оператор, открывший его следом, должен увидеть свою же запись.
 *
 * Ошибки тостятся здесь, успех — на месте вызова: текст успеха зависит от
 * действия, а текст ошибки приходит с бэкенда и одинаков для всех четырёх.
 */
export function useModerateComment(commentId: number) {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: qk.comment(commentId) });
    void queryClient.invalidateQueries({ queryKey: ["comments"] });
    void queryClient.invalidateQueries({ queryKey: qk.dashboard() });
    void queryClient.invalidateQueries({ queryKey: ["audit"] });
  };

  const onError = (error: unknown) => {
    void message.error(errorMessage(error));
  };

  const publish = useMutation({
    mutationFn: (input: { comment?: string }) =>
      apiPost("/api/v1/admin/comments/{comment_id}/publish", {
        params: { comment_id: commentId },
        body: input,
      }),
    onSuccess: invalidate,
    onError,
  });

  const hide = useMutation({
    mutationFn: (input: { comment: string }) =>
      apiPost("/api/v1/admin/comments/{comment_id}/hide", {
        params: { comment_id: commentId },
        body: input,
      }),
    onSuccess: invalidate,
    onError,
  });

  const remove = useMutation({
    mutationFn: (input: { comment: string }) =>
      apiPost("/api/v1/admin/comments/{comment_id}/delete", {
        params: { comment_id: commentId },
        body: input,
      }),
    onSuccess: invalidate,
    onError,
  });

  const reply = useMutation({
    mutationFn: (input: { text: string }) =>
      apiPost("/api/v1/admin/comments/{comment_id}/reply", {
        params: { comment_id: commentId },
        body: input,
      }),
    onSuccess: invalidate,
    onError,
  });

  return { publish, hide, remove, reply };
}
